import React, { forwardRef, useState, useRef, Component } from 'react';
import Grid from '@material-ui/core/Grid';
import ScheduleSelect, {
  Props as ScheduleSelectProps,
} from '../../core/FormField/ScheduleSelect';
import T from 'i18n-react';
import Button from '@material-ui/core/Button';
import { STARTDATE, ENDDATE, TAG, REGION } from './variables';
import useWidth from '../../core/Shared/useWidth';
import { isWidthUp } from '@material-ui/core/withWidth';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import SearchCategoryImage from '../../../images/front/search/search_category.png';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FilterSelect, { FilterSelectIcon } from '../Select/FilterSelect';
import RegionSelect from '../Select/RegionSelect';
import {
  UsersitePlansGetRegionTypePayload,
  UsersitePlansGetTagTypePayload,
} from '~/models/schema';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(1.5),
    },
    [theme.breakpoints.up('sm')]: {
      '& > *:not(:last-child)': {
        marginBottom: 0,
      },
    },
  },
  clearGrid: {
    textAlign: 'center',
  },
  clear: {
    color: theme.palette.text.secondary,
    textDecoration: 'underline',
    '&:hover': {
      color: theme.palette.text.secondary,
      backgroundColor: 'transparent',
    },
  },
}));

type TermSelectProps = Pick<
  ScheduleSelectProps,
  'startDate' | 'endDate' | 'onChange' | 'withPortal'
>;

const TermSelect = forwardRef(
  ({ startDate, endDate, onChange, withPortal }: TermSelectProps, ref) => (
    <ScheduleSelect
      label={
        T.translate(
          withPortal ? 'plan.scheduleSpPicker' : 'plan.schedulePicker',
        ) as string
      }
      startDate={startDate}
      endDate={endDate}
      withoutTrunc
      withPortal={withPortal}
      onChange={onChange}
      innerRef={ref}
    />
  ),
);

type TagSelectProps = {
  tags: Array<UsersitePlansGetTagTypePayload>;
  tag: number | null | undefined;
  onChange: (event: { [name: string]: unknown }) => void;
  withPortal: boolean;
};

const TagSelect = forwardRef(
  ({ tags, tag, onChange, withPortal }: TagSelectProps, ref) => {
    return (
      <FilterSelect
        choices={tags.map(({ id, name }) => ({
          text: name,
          value: id,
        }))}
        label={
          <>
            <FilterSelectIcon
              src={SearchCategoryImage}
              alt={T.translate('plan.tagSelect') as string}
            />
            {T.translate(withPortal ? 'plan.tagSpSelect' : 'plan.tagSelect')}
          </>
        }
        name={TAG}
        value={tag}
        onChange={onChange}
        ref={ref}
      />
    );
  },
);

type FiltersProps = {
  classes?: { [key: string]: string };
  tags: Array<UsersitePlansGetTagTypePayload>;
  regions: Array<UsersitePlansGetRegionTypePayload>;
  startDate?: moment.MomentInput;
  endDate?: moment.MomentInput;
  tag?: number | null | undefined;
  region?: number | null | undefined;
  onChange: (params: string[][] | Record<string, string>) => void;
  onClear?: () => void;
  disableClear?: boolean;
};

type FiltersState = {
  [TAG]?: number | null;
  [REGION]?: number | null;
  [STARTDATE]?: string | null;
  [ENDDATE]?: string | null;
};

const Filters = ({
  classes: classesProps,
  tags,
  regions,
  tag,
  region,
  startDate,
  endDate,
  onChange,
  onClear,
  disableClear,
}: FiltersProps) => {
  const classes = useStyles({ classes: classesProps });

  const [state, setState] = useState<FiltersState>(
    omitBy(
      {
        [TAG]: tag,
        [REGION]: region,
        [STARTDATE]: startDate,
        [ENDDATE]: endDate,
      },
      (val) => isNil(val) || val === '',
    ),
  );
  const tagRef = useRef();
  const regionRef = useRef();
  const termRef = useRef<Component<ScheduleSelectProps>>();
  const width = useWidth();
  const withPortal = !isWidthUp('sm', width);

  const handleChange = (data) => {
    const nextState = omitBy(
      { ...state, ...data },
      (val) => isNil(val) || val === '',
    );
    onChange?.(nextState);
    setState(nextState);
  };

  const clearValue = () => {
    [termRef].forEach((r) =>
      r.current?.setState({ value: '', startDate: '', endDate: '' }),
    );
  };

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12} md="auto">
        <TermSelect
          onChange={handleChange}
          startDate={state[STARTDATE]}
          endDate={state[ENDDATE]}
          withPortal={withPortal}
          ref={termRef}
        />
      </Grid>
      <Grid item xs={6} md="auto">
        <RegionSelect
          name={REGION}
          label={withPortal ? '行きたい場所' : T.translate('plan.regionSelect')}
          regions={regions}
          value={state[REGION]}
          withPortal={withPortal}
          onChange={handleChange}
          ref={regionRef}
        />
      </Grid>
      <Grid item xs={6} md="auto">
        <TagSelect
          tags={tags}
          tag={state[TAG]}
          onChange={handleChange}
          withPortal={withPortal}
          ref={tagRef}
        />
      </Grid>
      {!disableClear && Object.keys(state).length > 0 && (
        <Grid item xs={12} sm="auto" className={classes.clearGrid}>
          <Button
            className={classes.clear}
            onClick={() => {
              clearValue();
              setState({});
              onClear?.();
            }}
            disableRipple
          >
            {T.translate('clear')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default Filters;
